import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { API_BASE_URL } from '../utils/apiConfig';
import Swal from 'sweetalert2';


const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export useUser for backwards compatibility with existing components
export const useUser = () => {
  const { user, roles, hasRole, login, logout } = useAuth();
  return { user, roles, hasRole, login, logout };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [roles, setRoles] = useState(['guest']); // Preserve existing role system
  const [userVersion, setUserVersion] = useState(0); // Force re-renders when user data changes
  const [sessionExpired, setSessionExpired] = useState(false); // Track if session was forcefully expired

  // Refs to prevent duplicate notifications and track auth state
  const sessionExpiredNotificationShown = useRef(false);
  const wasAuthenticated = useRef(false);

  // Check authentication status on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Track when user becomes authenticated (for session expiry detection)
  useEffect(() => {
    if (isAuthenticated) {
      wasAuthenticated.current = true;
      sessionExpiredNotificationShown.current = false;
      setSessionExpired(false);
    }
  }, [isAuthenticated]);

  // Listen for session expiration events from apiClient
  useEffect(() => {
    const handleSessionExpired = () => {
      // Only show notification if user was previously authenticated and we haven't shown it yet
      if (wasAuthenticated.current && !sessionExpiredNotificationShown.current) {
        sessionExpiredNotificationShown.current = true;
        console.warn('Session expired - clearing auth state');

        // Clear auth state
        setUser(null);
        setIsAuthenticated(false);
        setRoles(['guest']);
        setSessionExpired(true);
        localStorage.removeItem("roles");
        wasAuthenticated.current = false;

        // Show user-friendly notification
        Swal.fire({
          icon: 'info',
          title: 'Session Expired',
          text: 'Your session has expired. Please sign in again to continue.',
          confirmButtonText: 'Sign In',
          confirmButtonColor: '#3085d6',
          allowOutsideClick: false,
        }).then((result) => {
          if (result.isConfirmed) {
            // Dispatch event for components that want to show login modal
            window.dispatchEvent(new CustomEvent('auth:show-login'));
          }
        });
      }
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, []);

  // Validate session on tab focus (catches expired sessions proactively)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // Only check if tab becomes visible and user was authenticated
      if (document.visibilityState === 'visible' && wasAuthenticated.current && isAuthenticated) {
        try {
          await apiClient.get('/auth/validate');
          // Session still valid, no action needed
        } catch (error) {
          // Session invalid - the apiClient interceptor will dispatch auth:session-expired
          console.warn('Session validation failed on tab focus');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  const checkAuthStatus = async () => {
    try {
      const response = await apiClient.get('/auth/validate');

      // Set basic user data from validate endpoint
      setUser(response.data.user);
      setIsAuthenticated(true);

      // Now fetch the full profile data which includes linked player info
      try {
        const profileResponse = await apiClient.get('/auth/profile');

        // Update user with complete profile data including player info
        const fullUserData = {
          ...response.data.user,
          ...profileResponse.data,
          // Map the player data to maintain backward compatibility
          player_id: profileResponse.data.player?.id || null
        };

        setUser(fullUserData);
      } catch (profileError) {
        // Continue with basic user data if profile fetch fails
      }

      setUserVersion(prev => prev + 1); // Force re-renders

      // Preserve existing role system
      const userRoles = response.data.user?.roles || ['player'];
      setRoles(userRoles);
      localStorage.setItem("roles", JSON.stringify(userRoles));

    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      setRoles(['guest']);
      localStorage.removeItem("roles");
    } finally {
      setLoading(false);
    }
  };

  // Backwards compatibility - support old login method
  const legacyLogin = (newRoles) => {
    setRoles(newRoles);
    localStorage.setItem("roles", JSON.stringify(newRoles));
  };

  const login = async (emailOrUsername, password, isAdminLogin = false) => {
    try {
      const requestBody = isAdminLogin
        ? { username: emailOrUsername, password }
        : { email: emailOrUsername, password };

      const response = await apiClient.post('/auth/login', requestBody);

      setUser(response.data.user);
      setIsAuthenticated(true);

      // Update roles
      const userRoles = response.data.user?.roles || ['player'];
      setRoles(userRoles);
      localStorage.setItem("roles", JSON.stringify(userRoles));

      // Refresh user data to get complete profile with player info
      await checkAuthStatus();

      // Force re-renders in components that depend on user data
      setUserVersion(prev => prev + 1);

      return { success: true, user: response.data.user };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Login failed'
      };
    }
  };

  const register = async (email, password, firstName, lastName) => {
    try {
      const response = await apiClient.post('/auth/register', {
        email,
        password,
        firstName,
        lastName
      });

      setUser(response.data.user);
      setIsAuthenticated(true);

      const userRoles = response.data.user?.roles || ['player'];
      setRoles(userRoles);
      localStorage.setItem("roles", JSON.stringify(userRoles));

      // Refresh user data to get complete profile with player info
      await checkAuthStatus();

      // Force re-renders in components that depend on user data
      setUserVersion(prev => prev + 1);

      return { success: true, user: response.data.user };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Registration failed'
      };
    }
  };

  const requestMagicLink = async (email) => {
    try {
      await apiClient.post('/auth/magic-link', { email });

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to send magic link'
      };
    }
  };

  const verifyMagicLink = async (token, email) => {
    try {
      const response = await apiClient.post('/auth/magic-verify', {
        token,
        email
      });

      setUser(response.data.user);
      setIsAuthenticated(true);

      const userRoles = response.data.user?.roles || ['player'];
      setRoles(userRoles);
      localStorage.setItem("roles", JSON.stringify(userRoles));

      // Refresh user data to get complete profile with player info
      await checkAuthStatus();

      return { success: true, user: response.data.user };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Invalid or expired magic link'
      };
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setRoles(['guest']);
      localStorage.removeItem("roles");
    }
  };

  const linkPlayer = async (playerId) => {
    try {
      const response = await apiClient.post('/auth/link-player', { playerId });

      if (response.message) {
        // Refresh user data after linking to get updated profile with player info
        await checkAuthStatus();

        // Force re-renders in components that depend on user data
        setUserVersion(prev => prev + 1);

        return { success: true };
      }

      return { success: false, error: 'Failed to link player' };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to link player'
      };
    }
  }; const updateProfile = async (profileData) => {
    try {
      const response = await apiClient.put('/auth/profile', profileData);

      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update profile'
      };
    }
  };

  const uploadProfilePicture = async (file) => {
    try {
      const formData = new FormData();
      formData.append('profilePicture', file);

      const response = await apiClient.post('/auth/profile/picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update user data with new profile picture
      setUser(prev => ({
        ...prev,
        profilePicture: response.data.profilePicture
      }));

      // Force re-renders in components that depend on user data
      setUserVersion(prev => prev + 1);

      return { success: true, profilePicture: response.data.profilePicture };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to upload profile picture'
      };
    }
  };

  const getProfile = async () => {
    try {
      const response = await apiClient.get('/auth/profile');
      return { success: true, profile: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to get profile'
      };
    }
  };

  const signUpForEvent = async (eventId) => {
    try {
      const response = await apiClient.post(`/auth/events/${eventId}/signup`, {});
      return { success: true, message: response.message };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to sign up for event'
      };
    }
  };

  const withdrawFromEvent = async (eventId) => {
    try {
      const response = await apiClient.delete(`/auth/events/${eventId}/signup`);
      return { success: true, message: response.message };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to withdraw from event'
      };
    }
  };

  // Role checking function (preserves existing functionality)
  const hasRole = (role) => roles.includes(role);

  const value = {
    // New auth system
    user,
    isAuthenticated,
    loading,
    userVersion, // Force re-renders when user data changes
    sessionExpired, // True if session was forcefully expired (for UI feedback)
    login,
    register,
    logout,
    requestMagicLink,
    verifyMagicLink,
    linkPlayer,
    updateProfile,
    uploadProfilePicture,
    getProfile,
    signUpForEvent,
    withdrawFromEvent,
    checkAuthStatus,

    // Backwards compatibility for existing components
    roles,
    hasRole,
    legacyLogin, // Support old login method
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
